import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ArrowLeft,
    Wallet as WalletIcon,
    Plus,
    History,
    ArrowUpRight,
    ArrowDownLeft,
    Sparkles,
    TrendingUp,
    ShieldCheck,
    Coins
} from 'lucide-react';
import DefaultSEO from '../components/DefaultSEO';

const Wallet = () => {
    const navigate = useNavigate();
    const [balance, setBalance] = useState(2500);
    const [addAmount, setAddAmount] = useState('');
    const [showSuccessToast, setShowSuccessToast] = useState(false);
    const [addedValue, setAddedValue] = useState(0);

    const [transactions, setTransactions] = useState([]);

    useEffect(() => {
        fetchWalletData();
    }, []);

    const fetchWalletData = async () => {
        try {
            const token = localStorage.getItem('auth_token');
            if (!token) return;
            const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/api/v1';
            const res = await fetch(`${apiBase}/wallet`, {
                headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
            });
            const data = await res.json();
            if (data.success && data.data) {
                setBalance(parseFloat(data.data.wallet.balance));
                setTransactions(data.data.transactions);
            }
        } catch (err) {
            console.error('Error fetching wallet:', err);
        }
    };

    const handleAddMoney = async (e) => {
        e.preventDefault();
        const amt = parseFloat(addAmount);
        if (isNaN(amt) || amt <= 0) return;

        try {
            const token = localStorage.getItem('auth_token');
            if (!token) return;
            const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/api/v1';
            const res = await fetch(`${apiBase}/wallet/add`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ amount: amt })
            });
            const data = await res.json();
            if (data.success && data.data) {
                setBalance(parseFloat(data.data.wallet.balance));
                setTransactions([data.data.transaction, ...transactions]);
                setAddedValue(amt);
                setAddAmount('');
                setShowSuccessToast(true);

                setTimeout(() => {
                    setShowSuccessToast(false);
                }, 3000);
            }
        } catch (err) {
            console.error('Error adding money to wallet:', err);
        }
    };

    const handleQuickSelect = (amt) => {
        setAddAmount(amt.toString());
    };

    return (
        <div className="container py-4 min-vh-100 d-flex flex-column align-items-center" style={{ maxWidth: '750px' }}>
            <DefaultSEO title="Zoyo Wallet | ZoyoTrip" />
            {/* Header */}
            <button
                className="btn align-self-start btn-link text-dark text-decoration-none p-0 mb-4 d-flex align-items-center"
                onClick={() => navigate('/account')}
            >
                <ArrowLeft size={20} className="me-2" />
                <h4 className="fw-bold mb-0">Zoyo Wallet</h4>
            </button>

            {/* Wallet Success Toast */}
            {showSuccessToast && (
                <div
                    className="position-fixed bottom-[80px] end-4 bg-emerald-600 text-white px-4 py-3 rounded-3 shadow-lg z-3 d-flex align-items-center gap-2 border border-emerald-500 animate-bounce"
                    style={{ transition: 'all 0.3s ease' }}
                >
                    <Coins className="w-5 h-5" />
                    <span className="fw-bold">₹{addedValue.toLocaleString('en-IN')} added successfully to your wallet!</span>
                </div>
            )}

            {/* Premium Wallet Balance Card */}
            <div
                className="w-100 rounded-4 p-4 text-white mb-4 position-relative overflow-hidden shadow-lg border border-opacity-10 border-white"
                style={{
                    background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #311042 100%)'
                }}
            >
                {/* Decorative glowing circles */}
                <div className="position-absolute bg-primary rounded-full opacity-20 filter blur-3xl" style={{ width: '150px', height: '150px', top: '-50px', right: '-50px' }}></div>
                <div className="position-absolute bg-emerald-500 rounded-full opacity-10 filter blur-3xl" style={{ width: '180px', height: '180px', bottom: '-80px', left: '-50px' }}></div>

                <div className="d-flex justify-content-between align-items-start mb-4">
                    <div>
                        <div className="d-flex align-items-center gap-2 mb-1">
                            <span className="small text-uppercase tracking-wider opacity-75 fw-bold">Active Balance</span>
                            <Sparkles size={14} className="text-yellow-400" />
                        </div>
                        <h1 className="display-5 fw-extrabold mb-0">₹{balance.toLocaleString('en-IN')}</h1>
                    </div>
                    <div className="bg-white bg-opacity-10 p-3 rounded-3 backdrop-blur-sm border border-white border-opacity-10">
                        <WalletIcon size={30} className="text-emerald-400" />
                    </div>
                </div>

                <div className="row g-2 border-top border-white border-opacity-10 pt-3">
                    <div className="col-6">
                        <span className="text-xs opacity-75 block mb-0.5">Cashback Earned</span>
                        <div className="fw-bold d-flex align-items-center gap-1">
                            <Coins size={14} className="text-yellow-400" />
                            ₹1,500
                        </div>
                    </div>
                    <div className="col-6 text-end">
                        <span className="text-xs opacity-75 block mb-0.5">Refunds Received</span>
                        <div className="fw-bold d-flex align-items-center gap-1 justify-content-end">
                            <TrendingUp size={14} className="text-emerald-400" />
                            ₹4,500
                        </div>
                    </div>
                </div>
            </div>

            {/* Quick Actions & Add Money */}
            <div className="card shadow-sm border-0 w-100 mb-4 rounded-4">
                <div className="card-body p-4">
                    <h5 className="fw-bold mb-3 d-flex align-items-center gap-2 text-dark">
                        <Plus className="text-primary" size={20} /> Add Money to Wallet
                    </h5>
                    <form onSubmit={handleAddMoney}>
                        <div className="input-group mb-3">
                            <span className="input-group-text bg-light fw-bold text-muted border-end-0">₹</span>
                            <input
                                type="number"
                                className="form-control bg-light border-start-0 ps-1 py-2.5 fw-semibold"
                                placeholder="Enter amount (e.g. 500)"
                                value={addAmount}
                                onChange={(e) => setAddAmount(e.target.value)}
                                min="1"
                                required
                            />
                            <button className="btn btn-primary px-4 fw-bold" type="submit">
                                Add Now
                            </button>
                        </div>

                        {/* Quick Select Buttons */}
                        <div className="d-flex flex-wrap gap-2 mb-2">
                            {[200, 500, 1000, 2000].map((amt) => (
                                <button
                                    key={amt}
                                    type="button"
                                    onClick={() => handleQuickSelect(amt)}
                                    className="btn btn-sm btn-outline-secondary rounded-pill px-3 py-1.5 fw-semibold"
                                >
                                    + ₹{amt}
                                </button>
                            ))}
                        </div>
                    </form>
                </div>
            </div>

            {/* Transaction History Section */}
            <div className="card shadow-sm border-0 w-100 rounded-4">
                <div className="card-body p-4">
                    <h5 className="fw-bold mb-4 d-flex align-items-center gap-2 text-dark">
                        <History className="text-primary" size={20} /> Transaction History
                    </h5>

                    {transactions.length === 0 ? (
                        <p className="text-muted text-center py-4">No transactions found.</p>
                    ) : (
                        <div className="d-flex flex-column gap-3">
                            {transactions.map((txn) => {
                                const isCredit = txn.type === 'credit';
                                return (
                                    <div key={txn.id} className="d-flex justify-content-between align-items-center p-3 rounded-3 bg-light border border-gray-100">
                                        <div className="d-flex align-items-center gap-3">
                                            <div className={`p-2.5 rounded-3 d-flex items-center justify-center ${isCredit ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'
                                                }`}>
                                                {isCredit ? <ArrowDownLeft size={20} /> : <ArrowUpRight size={20} />}
                                            </div>
                                            <div>
                                                <h6 className="fw-bold mb-0.5 text-gray-900 small">{txn.title}</h6>
                                                <div className="d-flex align-items-center gap-2 text-xs text-muted">
                                                    <span>{txn.date}</span>
                                                    <span>•</span>
                                                    <span className="font-mono text-uppercase">{txn.id}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="text-end">
                                            <span className={`fw-extrabold fs-6 ${isCredit ? 'text-emerald-600' : 'text-red-500'}`}>
                                                {isCredit ? '+' : '-'} ₹{txn.amount.toLocaleString('en-IN')}
                                            </span>
                                            <span className="d-block text-[10px] text-emerald-600 fw-semibold bg-emerald-50 px-2 py-0.5 rounded-pill mt-1 border border-emerald-100">
                                                {txn.status}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {/* Trust and Safety Banner */}
            <div className="d-flex align-items-center gap-2 text-muted mt-4 justify-content-center">
                <ShieldCheck className="text-emerald-500" size={16} />
                <span className="text-xs fw-semibold">100% secure bookings with ZoyoTrust Guarantee</span>
            </div>
        </div>
    );
};

export default Wallet;